import CrudModule from "../components/CrudModule";
import { Link as LinkIcon, UserFocus, ClipboardText, Sparkle, MapPin, ArrowClockwise, ClockCounterClockwise, ShieldWarning, ShieldCheck, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import client from "../lib/api";
import { X, CheckCircle } from "@phosphor-icons/react";
import PasteLoadModal from "./PasteLoadModal";
import FuelCostEstimateCard from "../components/FuelCostEstimateCard";
import CustomerCreditBadge from "../components/CustomerCreditBadge";
import LoadTimelineDrawer from "../components/LoadTimelineDrawer";
import ReadyToInvoiceCard from "../components/ReadyToInvoiceCard";
import PodAgeHeatmap from "../components/PodAgeHeatmap";
import HoursOfServiceRoster from "../components/HoursOfServiceRoster";
import LiveTruckMap from "../components/LiveTruckMap";

const STATUS = [
  { value: "pending", label: "Pending" },
  { value: "assigned", label: "Assigned" },
  { value: "in_transit", label: "In transit" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

export default function DispatchPage() {
  const [suggestFor, setSuggestFor] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [timelineFor, setTimelineFor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [slaByLoadId, setSlaByLoadId] = useState({});
  const [slaLateCount, setSlaLateCount] = useState(0);

  // Poll SLA status every 60s — colour-codes late loads red and shows a
  // dispatch-wide "N late" pill in the header. Fails silently: a stale
  // ELD or a pilot without any ELD at all just means every row falls
  // back to the neutral border.
  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const { data } = await client.get("/loads/sla-status");
        if (!alive) return;
        const byId = {};
        for (const row of (data.loads || [])) byId[row.load_id] = row;
        setSlaByLoadId(byId);
        setSlaLateCount(data.late_count || 0);
      } catch { /* silent */ }
    }
    tick();
    const iv = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, [refreshKey]);

  return (
    <>
      {/* Ready-to-Invoice — POD received, no invoice yet. Money on the table. */}
      <div className="px-4 sm:px-8 pt-4 sm:pt-8 max-w-[1400px]">
        <ReadyToInvoiceCard compact />
      </div>

      {/* Samsara HOS roster — auto-hides when not connected */}
      <div className="px-4 sm:px-8 pt-4 max-w-[1400px]">
        <HoursOfServiceRoster />
      </div>

      {/* Samsara Live GPS Map — auto-hides when not connected */}
      <div className="px-4 sm:px-8 pt-4 max-w-[1400px]">
        <LiveTruckMap />
      </div>

      {/* Predicted-late banner — shows only when at least one in-motion
          load is projected to arrive after its delivery_date. Ops can
          jump straight to the offending rows and re-plan. */}
      {slaLateCount > 0 && (
        <div className="px-4 sm:px-8 pt-4 max-w-[1400px]">
          <div
            className="rounded-xl border border-red-500/30 bg-red-500/[0.06] p-4 flex items-center gap-3"
            data-testid="dispatch-sla-banner"
          >
            <div className="shrink-0 w-9 h-9 rounded-lg bg-red-500/20 text-red-300 flex items-center justify-center">
              <ClockCounterClockwise size={18} weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold">
                SLA · Late-runner alert
              </div>
              <div className="text-sm text-white mt-0.5">
                {slaLateCount} in-motion load{slaLateCount === 1 ? "" : "s"} projected to arrive after the delivery date — reschedule or update the customer.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Paste-from-Load-Board callout */}
      <div className="px-4 sm:px-8 pt-4 sm:pt-8 max-w-[1400px]">
        <div className="rounded-xl border border-[#FF5A1F]/25 bg-gradient-to-r from-[#FF5A1F]/10 via-[#FF5A1F]/[0.04] to-transparent p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="shrink-0 w-11 h-11 rounded-lg bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center">
              <ClipboardText size={22} weight="fill" />
            </div>
            <div>
              <div className="text-white font-semibold text-sm sm:text-base flex items-center gap-2">
                Paste from Load Board
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF5A1F] text-black rounded-full px-1.5 py-0.5">AI</span>
              </div>
              <div className="text-neutral-400 text-xs sm:text-sm mt-0.5">
                Copy any load from DAT, Truckstop, broker emails, or SMS — AI extracts everything in 2 seconds.
              </div>
            </div>
          </div>
          <button
            onClick={() => setPasteOpen(true)}
            data-testid="dispatch-paste-from-loadboard"
            className="shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center gap-2"
          >
            <Sparkle size={14} weight="fill" /> Paste Load
          </button>
        </div>
      </div>

      <CrudModule
        key={refreshKey}
        testidPrefix="dispatch"
        resource="loads"
        overline="DISPATCH BOARD"
        title="Loads & Routes"
        subtitle="Assign, track, and share public tracking links with customers."
        aiCategory="dispatch_notification"
        aiContextFields={["load_number", "origin", "destination", "pickup_date", "delivery_date", "customer", "notes"]}
        primaryField="load_number"
        initialForm={{ status: "pending", rate: 0 }}
        extraRowActions={(row) => [
          {
            icon: <ClockCounterClockwise size={16} weight="regular" />,
            onClick: () => setTimelineFor(row),
            tooltip: "Delivery timeline",
            testid: `dispatch-timeline-${row.id}`,
            color: "info",
          },
          {
            icon: <UserFocus size={16} weight="regular" />,
            onClick: () => setSuggestFor(row),
            tooltip: "AI suggest driver",
            testid: `dispatch-suggest-${row.id}`,
            color: "primary",
          },
          {
            icon: <ArrowClockwise size={16} weight="regular" />,
            onClick: async () => {
              try {
                const { data } = await client.post(`/loads/${row.id}/recalc-miles`);
                const src = data.source === "ors" ? "HGV route" : "great-circle × 1.20";
                toast.success(`${data.miles} mi · ${data.origin_state} → ${data.destination_state} (${src})`);
                setRefreshKey((k) => k + 1);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Recalc failed — check City, ST format");
              }
            },
            tooltip: "Auto-calculate miles",
            testid: `dispatch-recalc-miles-${row.id}`,
            color: "info",
          },
          {
            icon: <LinkIcon size={16} weight="regular" />,
            onClick: () => {
              const url = `${window.location.origin}/track/${row.id}`;
              navigator.clipboard.writeText(url);
              toast.success("Tracking link copied — share it with your customer");
            },
            tooltip: "Copy customer tracking link",
            testid: `dispatch-track-${row.id}`,
            color: "info",
          },
        ]}
        fields={[
          { key: "load_number", label: "Load #", required: true, placeholder: "LD-1001" },
          { key: "customer", label: "Customer", required: true, placeholder: "Acme Corp" },
          { key: "origin", label: "Origin", required: true, placeholder: "Dallas, TX" },
          { key: "destination", label: "Destination", required: true, placeholder: "Atlanta, GA" },
          { key: "pickup_date", label: "Pickup", type: "date", required: true },
          { key: "delivery_date", label: "Delivery", type: "date", required: true },
          { key: "rate", label: "Rate ($)", type: "number" },
          { key: "miles", label: "Miles (auto-calc if blank)", type: "number", placeholder: "0 = auto" },
          { key: "status", label: "Status", type: "select", options: STATUS },
          { key: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        columns={[
          { key: "load_number", label: "LOAD #", render: (r) => <span className="font-mono text-xs">{r.load_number}</span> },
          { key: "route", label: "ROUTE", render: (r) => (
            <div className="flex flex-col gap-0.5">
              <span>{r.origin} <span className="text-neutral-600">→</span> {r.destination}</span>
              {(r.origin_state || r.destination_state) && (
                <span className="text-[10px] text-neutral-500 font-mono">
                  {r.origin_state || "??"} → {r.destination_state || "??"}
                </span>
              )}
            </div>
          ) },
          { key: "customer", label: "CUSTOMER", render: (r) => (
            <div className="inline-flex items-center gap-2">
              <CustomerCreditBadge name={r.customer} />
              <span>{r.customer}</span>
            </div>
          ) },
          { key: "miles", label: "MILES", render: (r) => {
            const mi = Number(r.miles || 0);
            if (!mi) return <span className="text-neutral-700 text-xs">—</span>;
            const isAuto = r.miles_source === "haversine" || r.miles_source === "ors";
            return (
              <div className="inline-flex items-center gap-1.5">
                <span className="font-mono text-xs">{Math.round(mi).toLocaleString()}</span>
                {isAuto && <MapPin size={11} weight="fill" className="text-emerald-400" title={`Auto: ${r.miles_source}`} />}
              </div>
            );
          } },
          { key: "rate", label: "RATE", render: (r) => <span className="font-mono">${Number(r.rate || 0).toLocaleString()}</span> },
          { key: "status", label: "STATUS", render: (r) => <SlaStatusPill row={r} sla={slaByLoadId[r.id]} /> },
        ]}
        renderFormExtras={(form) => (
          <FuelCostEstimateCard miles={form.miles} rate={form.rate} />
        )}
      />

      {/* POD Age Heatmap — auto-hides on zero-signal accounts */}
      <div className="px-4 sm:px-8 pb-6 max-w-[1400px]">
        <PodAgeHeatmap />
      </div>

      {suggestFor && <SuggestDriverModal load={suggestFor} onClose={() => setSuggestFor(null)} />}
      <PasteLoadModal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
      <LoadTimelineDrawer
        loadId={timelineFor?.id}
        open={!!timelineFor}
        onClose={() => setTimelineFor(null)}
      />
    </>
  );
}

function SuggestDriverModal({ load, onClose }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await client.post(`/loads/${load.id}/suggest-drivers`);
        if (!mounted) return;
        setSuggestions(data.suggestions || []);
        if (!data.suggestions?.length && data.message) toast.info(data.message);
      } catch {
        if (mounted) toast.error("Could not generate suggestions");
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [load.id]);

  const assign = async (driverId, driverName) => {
    setAssigning(driverId);
    try {
      await client.patch(`/loads/${load.id}`, { driver_id: driverId, status: "assigned" });
      toast.success(`Assigned to ${driverName}`);
      onClose();
      window.location.reload();
    } catch { toast.error("Assignment failed"); }
    finally { setAssigning(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="suggest-driver-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FF5A1F]/15 border border-[#FF5A1F]/30 flex items-center justify-center">
              <Sparkle size={18} weight="fill" color="#FF5A1F" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">AI Driver Match</div>
              <div className="text-xs text-neutral-500">Load #{load.load_number} · {load.origin} → {load.destination}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6 min-h-[200px]">
          {loading ? (
            <div className="text-center py-12"><span className="stream-cursor text-neutral-400">AI ranking your drivers</span></div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-8 text-neutral-500 text-sm">
              No suggestions available. Add drivers to your roster first.
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((s, i) => (
                <div key={s.driver_id} data-testid={`suggestion-${i}`} className="p-4 rounded-md bg-[#141414] border border-white/10 hover:border-[#FF5A1F]/30 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-xs text-neutral-500">#{i + 1}</span>
                        <span className="font-medium text-neutral-100">{s.driver_name}</span>
                        <span className="pill pill-primary">SCORE {s.score}/10</span>
                        <HosBadge hos={s.hos} />
                      </div>
                      <p className="text-sm text-neutral-400 leading-relaxed">{s.reasoning}</p>
                    </div>
                    <button
                      onClick={() => assign(s.driver_id, s.driver_name)}
                      disabled={assigning !== null || s?.hos?.severity === "blocked"}
                      data-testid={`assign-btn-${i}`}
                      title={s?.hos?.severity === "blocked" ? "HOS blocked — driver can't legally finish this load" : undefined}
                      className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {assigning === s.driver_id ? "..." : <><CheckCircle size={12} weight="bold" /> Assign</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * HosBadge — renders a colored pill next to a suggested driver showing
 * whether they have enough Hours-of-Service to legally finish the trip.
 *
 * Renders nothing when:
 *   - no HOS data (`hos` prop missing/undefined)
 *   - Samsara/Motive not connected (`hos.available === false`)
 * so the modal degrades gracefully for ops teams that haven't wired an
 * ELD yet.
 *
 * Colors map to `core.hos_aware_dispatch` severity:
 *   ok      → emerald  (comfortable buffer)
 *   tight   → amber    (fits but <60min of headroom)
 *   blocked → red      (short — driver can't legally finish)
 */
function HosBadge({ hos }) {
  if (!hos || !hos.available) return null;
  const sev = hos.severity;

  const cfg = {
    ok:      { icon: <ShieldCheck size={11} weight="fill" />, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "HOS OK" },
    tight:   { icon: <Clock size={11} weight="fill" />,       cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "HOS TIGHT" },
    blocked: { icon: <ShieldWarning size={11} weight="fill" />, cls: "bg-red-500/15 text-red-300 border-red-500/30", label: "HOS BLOCKED" },
  }[sev] || { icon: <Clock size={11} weight="regular" />, cls: "bg-neutral-500/15 text-neutral-400 border-neutral-500/30", label: "HOS ?" };

  const remaining = Number.isFinite(hos.drive_hours_remaining)
    ? `${hos.drive_hours_remaining.toFixed(1)}h drive left`
    : "";

  return (
    <span
      data-testid={`hos-badge-${sev || "unknown"}`}
      className={`pill inline-flex items-center gap-1 border ${cfg.cls}`}
      title={hos.reason || undefined}
    >
      {cfg.icon}
      <span className="font-bold tracking-wide">{cfg.label}</span>
      {remaining && <span className="opacity-70 font-mono">· {remaining}</span>}
    </span>
  );
}



/**
 * SlaStatusPill — renders the load status alongside a red "LATE"
 * badge when the predicted ETA has slipped past the delivery date.
 *
 * Falls back to plain status text when:
 *   - No SLA data yet (endpoint hasn't returned, or load isn't in-motion)
 *   - Load isn't late
 * so the column stays functional for pilots without any ELD wired up.
 */
function SlaStatusPill({ row, sla }) {
  const status = row?.status || "—";
  if (!sla || !sla.is_late) {
    return <span data-testid={`dispatch-status-${row.id}`}>{status}</span>;
  }
  const slipMin = sla.slip_minutes || 0;
  const slipDisplay =
    slipMin < 60
      ? `${slipMin}m`
      : `${Math.floor(slipMin / 60)}h ${slipMin % 60 ? `${slipMin % 60}m` : ""}`;
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`dispatch-status-${row.id}`}>
      <span>{status}</span>
      <span
        className="pill bg-red-500/15 text-red-300 border border-red-500/30 inline-flex items-center gap-1"
        data-testid={`dispatch-sla-late-${row.id}`}
        title={`Predicted arrival is ~${slipDisplay} past the delivery date`}
      >
        LATE · {slipDisplay}
      </span>
    </div>
  );
}
